import useAxios from "axios-hooks";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import AsyncSelect from "react-select/async";

type Classroom = {
  _id: string;
  name: string;
};

type Option = {
  label: string;
  value: string;
} & Classroom;

type Props = {
  value: string[]; // array of selected IDs
  onChange: (ids: string[]) => void;
  isMulti?: boolean;
  placeholder?: string;
  defaultSelectedOptions?: Option[];
};

export function ClassroomSelect({
  value,
  onChange,
  isMulti = true,
  placeholder,
  defaultSelectedOptions,
}: Props) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(
    defaultSelectedOptions ? defaultSelectedOptions : []
  );
  const [, getClassrooms] = useAxios(
    {
      url: "/classrooms",
    },
    {
      manual: true,
    }
  );

  // Detect dark mode once
  useEffect(() => {
    setIsDarkMode(localStorage.getItem("theme") === "dark");
  }, []);

  // Used for async search
  const loadOptions = useCallback(
    async (inputValue: string) => {
      try {
        const res = await getClassrooms({
          params: { search: inputValue },
        });

        const selectedIds = new Set(value);
        const options = res.data.docs
          .filter((c: Classroom) => !selectedIds.has(c._id))
          .map((c: Classroom) => ({
            label: c.name,
            value: c._id,
            ...c,
          }));

        return options;
      } catch (err) {
        console.error("Failed to search classrooms", err);
        return [];
      }
    },
    [value, getClassrooms]
  );

  const handleChange = (options: Option[] | null) => {
    const selected = options ?? [];
    setSelectedOptions(selected);
    onChange(selected.map((opt) => opt._id));
  };

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: isDarkMode ? "#181818" : "white",
      borderColor: state.isFocused
        ? "#f51f6f"
        : isDarkMode
        ? "#282828"
        : "#d1d5db",
      color: isDarkMode ? "white" : "black",
      boxShadow: state.isFocused ? `0 0 0 1px #f51f6f` : "none",
      "&:hover": {
        borderColor: "#f51f6f",
      },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? "#181818" : "white",
      color: isDarkMode ? "white" : "black",
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused
        ? isDarkMode
          ? "#f51f6f"
          : "#fbb6ce"
        : isDarkMode
        ? "#181818"
        : "white",
      color: state.isFocused ? "white" : isDarkMode ? "white" : "black",
      cursor: "pointer",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: isDarkMode ? "#9ca3af" : "#6b7280",
    }),
    singleValue: (base: any) => ({
      ...base,
      color: isDarkMode ? "white" : "black",
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? "#282828" : "#fce7f3",
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: isDarkMode ? "white" : "#f51f6f",
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: isDarkMode ? "#e11d48" : "#c026d3",
      ":hover": {
        backgroundColor: isDarkMode ? "#e11d48" : "#fbcfe8",
        color: "white",
      },
    }),
  };

  return (
    <AsyncSelect
      isMulti={isMulti}
      value={selectedOptions}
      onChange={handleChange as any}
      loadOptions={loadOptions}
      placeholder={placeholder}
      cacheOptions
      defaultOptions
      styles={customStyles}
      classNamePrefix="react-select"
    />
  );
}
